import React, { useMemo, useEffect, useState, useCallback } from "react";
import styled from '@emotion/styled';
import { isMobile } from 'react-device-detect';
import Card from '../components/Ui/Card.jsx';
import { fetchSightTests } from "../http/reporting.js";
import { fetchLinkedAccounts } from "../http/account.js";
import {Alert} from "../components/Ui/Alert.jsx";
import { ServerSidePagingTable } from "../components/Ui/Table.jsx";
import { formatDateToDDMMYYYY } from "../components/Helpers/dateFns.js";

const CenterContainer = styled.div`
  display: flex;
  justify-content: center;
  height: 100vh;
  width: 100%;
`;

const FormContainer = styled.div`
  border-radius: 5px;
  background-color: #fff;
  width: ${isMobile ? '100%' : '50%'};
`;

const Heading = styled.h3`
  margin: 0;
  margin-top: 10px;
  padding-bottom: 5px;
`;

const Paragraph = styled.p`
  padding-top: 10px;
  margin: 0 0 20px 0;
  font-size:15px;
`;

export default function SightTestHistory({ colour, email }) {
  const [linkedAccounts, setLinkedAccounts] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const [sightTests, setSightTests] = useState([]);
  const [sightTestCount, setSightTestCount] = useState(0);
  const [pagingTracker, setPagingTracker] = useState(
    {
      currentPage: 1,
      pageSize: 10,
      filterStatus: null,
      searchValue: null,
      account: null,
      coWorker: null
    });

  useEffect(() => {
    const loadLinkedAccounts = async () => {
      setIsLoading(true);
      try {
        const accounts = await fetchLinkedAccounts(email);
        const userAccounts = [];

        accounts.forEach(account => {
          userAccounts.push({ label: account.accountdescrip, value: account.accountid });
        });

        setLinkedAccounts(userAccounts);
        setError(null);
      } catch (e) {
        setError(e);
      } finally {
        setIsLoading(false);
      }
    };

    loadLinkedAccounts();
  }, [email]);

  const loadSightTests = useCallback(async (account, page, pageSize, filterStatus = null, searchValue = null, coWorker = null) => {
    setIsLoading(true);

    if (account || linkedAccounts) {
      try {
        const fetchedSightTests = await fetchSightTests((account ? account : linkedAccounts[0].value), page, pageSize, searchValue);

        if (!fetchedSightTests[0].History.length) {
          setSightTests([]);
          setSightTestCount(0);
        }

        if (fetchedSightTests && fetchedSightTests[0].History[0].SightTestDetails) {
          const restructuredSightTests = fetchedSightTests[0].History[0].SightTestDetails.map(sightTest => ({
            ...sightTest,
            sightTestTitle: `${sightTest.christname} ${sightTest.surname}`,
            raisedDate: formatDateToDDMMYYYY(sightTest.prescriptiondate),
            dueDate: formatDateToDDMMYYYY(sightTest.recalldate)
          }));
          
          setSightTests(restructuredSightTests);
          setSightTestCount(fetchedSightTests[0].SightTestCount);
          
        } 
        
        setError(null);
      } catch (e) {
        setError(e);
        setSightTests([]);
          setSightTestCount(0);
      } finally {
        setIsLoading(false);
      }
    }
  }, [email, linkedAccounts]);

  const columns = useMemo(
    () => [
      {
        Header: "Name",
        accessor: "sightTestTitle",
      },
      {
        Header: "Sight Test",
        accessor: "raisedDate",
      },
      {
        Header: "Follow-Up Test Due",
        accessor: "dueDate",
      },
      {
        Header: "Period (months)",
        accessor: "recallperiod",
      },
      {
        Header: "Due (months)",
        accessor: "MonthsDue",
      },
    ]
  );

  return (
    <CenterContainer>
      <FormContainer>
        <Card
          label={'Prescription Sight Test History'}
          headercolour={colour.sidebar}
          headerTextColour={colour.font}>
          <Heading>Prescription Sight Test History</Heading>
          <Paragraph>This feature allows you to view the history of your employees' sight tests. The information is based on the date of the sight test entered by the optician when processing the employee's EyeTicket. The optician also provides their recommended period in months until the employee's next sight test is due.</Paragraph>
          
          <ServerSidePagingTable
            columns={columns}
            pagingFn={loadSightTests}
            data={sightTests}
            recordCount={sightTestCount}
            setPagingTracker={setPagingTracker}
            arrAccountFilters={linkedAccounts} />

          {(error && !linkedAccounts) && <Alert type="error">Unable to render page due to <b>{error.message}</b></Alert>}
          {!sightTestCount && <Alert type="information">No sight test information found.</Alert>}
        </Card>
      </FormContainer>
    </CenterContainer>
  );
}